import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { getSliderStyles, ISliderProps } from './Slider.model';
import {
	StyledCenterLine,
	StyledContainer,
	StyledSlider,
	StyledSliderBackgroudWrapper,
	StyledSliderBackground,
	StyledSliderContainer,
	StyledSliderWrapper,
	StyledValueInput,
} from './Slider.styled';

const Slider: React.FC<ISliderProps> = ({
	min,
	max,
	value,
	disabled,
	step = 0.01,
	width = 160,
	onChange,
	valueInput,
	center,
}: ISliderProps) => {
	const [inputFocus, setInputFocus] = useState<boolean>(false);
	const [inputValue, setInputValue] = useState<string>(String(value));
	const backgroundRef = useRef<HTMLDivElement>(null);
	const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	const onSlide = (e: React.ChangeEvent<HTMLInputElement>) => {
		const sliderValue = Number(e.target.value);
		onChange(sliderValue);
	};

	const minValue = useMemo(() => (typeof min === 'number' ? min : -1), [min]);
	const maxValue = useMemo(() => (typeof max === 'number' ? max : 1), [max]);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const { value } = e.target;

			if (
				!value ||
				value.split('.').length <= 2 ||
				value.split('.')[1]?.length <= 2
			) {
				if (parseFloat(value) < minValue || !value) {
					onChange(minValue);
				} else if (parseFloat(value) > maxValue) {
					onChange(maxValue);
				} else {
					onChange(parseFloat(value));
				}

				setInputValue(value);
			}
		},
		[minValue, maxValue, onChange],
	);

	useEffect(() => {
		if (!backgroundRef.current) return;

		if (animationTimeoutRef.current) {
			clearTimeout(animationTimeoutRef.current);
			animationTimeoutRef.current = null;
		}

		if (value === minValue || value === maxValue) {
			if (value === minValue) {
				backgroundRef.current.style.transition =
					'left 0.1s ease, width 0.1s ease';
				animationTimeoutRef.current = setTimeout(() => {
					if (backgroundRef.current) {
						backgroundRef.current.style.left = '0px';
						backgroundRef.current.style.width = center
							? `${Math.ceil(width / 2 - 2)}px`
							: '0px';
					}
					animationTimeoutRef.current = null;
				}, 100);
				return;
			}

			if (value === maxValue) {
				backgroundRef.current.style.transition = 'width 0.1s ease';
				animationTimeoutRef.current = setTimeout(() => {
					if (backgroundRef.current) {
						backgroundRef.current.style.width = center
							? `${Math.ceil(width / 2)}px`
							: `${width - 6}px`;
					}
					animationTimeoutRef.current = null;
				}, 100);
				return;
			}
		} else {
			backgroundRef.current.style.transition = 'none';
			const styles = getSliderStyles(
				value,
				minValue,
				maxValue,
				width - 10,
				center,
			);
			const leftVal = styles.left as unknown as number | string | undefined;
			const widthVal = styles.width as unknown as number | string | undefined;
			if (typeof leftVal === 'number') {
				backgroundRef.current.style.left = `${leftVal}px`;
			} else if (typeof leftVal === 'string') {
				backgroundRef.current.style.left = leftVal;
			}
			if (typeof widthVal === 'number') {
				backgroundRef.current.style.width = `${widthVal - (center ? 0 : 2)}px`;
			} else if (typeof widthVal === 'string') {
				backgroundRef.current.style.width = widthVal;
			}
		}
	}, [value, minValue, maxValue, center, width, backgroundRef]);

	useEffect(() => {
		return () => {
			if (animationTimeoutRef.current) {
				clearTimeout(animationTimeoutRef.current);
				animationTimeoutRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		if (!inputFocus) {
			setInputValue(String(parseFloat(value.toString()).toFixed(2)));
		}
	}, [value, inputFocus, center]);

	return (
		<StyledContainer>
			<StyledSliderWrapper>
				<StyledSliderBackgroudWrapper>
					<StyledSliderBackground
						$center={center}
						style={getSliderStyles(
							value,
							minValue,
							maxValue,
							width - 10,
							center,
						)}
						ref={backgroundRef}
					/>
				</StyledSliderBackgroudWrapper>
				<StyledSliderContainer $width={width}>
					<StyledSlider
						onChange={onSlide}
						value={value}
						min={minValue}
						max={maxValue}
						step={step}
						disabled={disabled}
						tabIndex={-1}
					/>
				</StyledSliderContainer>
				{center && <StyledCenterLine $width={width} />}
			</StyledSliderWrapper>
			{valueInput && (
				<StyledValueInput
					type={'number'}
					step={step < 0.01 ? 0.01 : step}
					min={minValue}
					max={maxValue}
					value={inputValue}
					onChange={handleInputChange}
					onFocus={() => setInputFocus(true)}
					onBlur={() => setInputFocus(false)}
					tabIndex={-1}
					onMouseEnter={() => {
						if (!disabled) {
							playSound(Sound.LinkCursor);
						}
					}}
				/>
			)}
		</StyledContainer>
	);
};

export { Slider };
